import { AttributeModel } from '../../model/AttributeModel'
import './ConformityLinkWidget.scss'
import LinkedItemsGroupByType from './LinkedItemsGroupByType'
import {getLinkedItemsByType, groupByDefinitionId} from '../../utils/sortedDefinitions'
import {ItemTypeEnum} from '../../../item/model/ItemType'
import {DisplaySet} from '../../model/DisplaySet'
import {SimpleItem} from '../../../item/model/SimpleItem'

const BACKWARD_LINK_DEFINITION_ID = '0fb85e6e-5385-5a74-9b72-0944486d4be3'
const FORWARD_LINK_DEFINITION_ID = 'd9a16476-e494-53cd-9454-41aa0489ae9f'

const AbstractItem = ({attributes}: {attributes: AttributeModel[]}) => {
    const definitionMap = groupByDefinitionId(attributes)
    const linkedConformItemsBackward = definitionMap.get(BACKWARD_LINK_DEFINITION_ID)?.attributes ?? []
    const linkedConformItemsForward = definitionMap.get(FORWARD_LINK_DEFINITION_ID)?.attributes ?? []

    const conformingManufacturerItems = getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.MANUFACTURER)
    let conformingCompanyItems = getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.COMPANY)
    conformingCompanyItems = [...conformingCompanyItems, ...getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.SINGLESOURCE)]
    conformingCompanyItems = [...conformingCompanyItems, ...getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.MULTISOURCE)]
    const isConformedToManufacturerItems = getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.MANUFACTURER)
    let isConformedToCompanyItems = getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.COMPANY)
    isConformedToCompanyItems = [...isConformedToCompanyItems, ...getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.SINGLESOURCE)]
    isConformedToCompanyItems = [...isConformedToCompanyItems, ...getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.MULTISOURCE)]

    return (
        <>
            <LinkedItemsGroupByType
                title="Conforming to this item"
                linkedItems={[...isConformedToManufacturerItems, ...isConformedToCompanyItems]}
            />
            <LinkedItemsGroupByType
                title="Conforms to these items"
                linkedItems={[...conformingCompanyItems, ...conformingManufacturerItems]}
            />
        </>
    )
}

const CompanyItem = ({attributes}: {attributes: AttributeModel[]}) => {
    const definitionMap = groupByDefinitionId(attributes)
    const linkedConformItemsBackward = definitionMap.get(BACKWARD_LINK_DEFINITION_ID)?.attributes ?? []
    const linkedConformItemsForward = definitionMap.get(FORWARD_LINK_DEFINITION_ID)?.attributes ?? []

    const conformingManufacturerItems = getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.MANUFACTURER)
    const conformingAbstractItems = getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.ABSTRACT)
    const isConformedToManufacturerItems = getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.MANUFACTURER)
    const isConformedToAbstractItems = getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.ABSTRACT)

    return (
        <>
            <LinkedItemsGroupByType
                title="Conforming to this item"
                linkedItems={[...conformingManufacturerItems, ...conformingAbstractItems]}
            />
            <LinkedItemsGroupByType title="Conforms to these specifications" linkedItems={isConformedToAbstractItems} />
            <LinkedItemsGroupByType title="Conforms to these items" linkedItems={isConformedToManufacturerItems} />
        </>
    )
}

const ManufacturerItem = ({attributes}: {attributes: AttributeModel[]}) => {
    const definitionMap = groupByDefinitionId(attributes)
    const linkedConformItemsBackward = definitionMap.get(BACKWARD_LINK_DEFINITION_ID)?.attributes ?? []
    const linkedConformItemsForward = definitionMap.get(FORWARD_LINK_DEFINITION_ID)?.attributes ?? []

    let conformingCompanyItems = getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.COMPANY)
    conformingCompanyItems = [...conformingCompanyItems, ...getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.SINGLESOURCE)]
    conformingCompanyItems = [...conformingCompanyItems, ...getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.MULTISOURCE)]
    const conformingAbstractItems = getLinkedItemsByType(linkedConformItemsBackward, ItemTypeEnum.ABSTRACT)
    let isConformedToCompanyItems = getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.COMPANY)
    isConformedToCompanyItems = [...isConformedToCompanyItems, ...getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.SINGLESOURCE)]
    isConformedToCompanyItems = [...isConformedToCompanyItems, ...getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.MULTISOURCE)]
    const isConformedToAbstractItems = getLinkedItemsByType(linkedConformItemsForward, ItemTypeEnum.ABSTRACT)

    return (
        <>
            <LinkedItemsGroupByType
                title="Conforming to this item"
                linkedItems={[...conformingCompanyItems, ...conformingAbstractItems]}
            />
            <LinkedItemsGroupByType title="Conforms to these items" linkedItems={isConformedToCompanyItems} />
            <LinkedItemsGroupByType title="Conforms to these specifications" linkedItems={isConformedToAbstractItems} />
        </>
    )
}

const ConformityLinkWidget = ({displaySet, parentItem}: {displaySet: DisplaySet; parentItem?: SimpleItem}) => {
    const getWidgetByItemType = (type: string | undefined) => {
        switch (type) {
            case ItemTypeEnum.COMPANY:
                return <CompanyItem attributes={displaySet.attributes} />
            case ItemTypeEnum.MULTISOURCE:
                return <CompanyItem attributes={displaySet.attributes} />
            case ItemTypeEnum.SINGLESOURCE:
                return <CompanyItem attributes={displaySet.attributes} />
            case ItemTypeEnum.MANUFACTURER:
                return <ManufacturerItem attributes={displaySet.attributes} />
            case ItemTypeEnum.ABSTRACT:
                return <AbstractItem attributes={displaySet.attributes} />
            default:
                return <></>
        }
    }

    return <div className="conforming-container">{getWidgetByItemType(parentItem?.type?.label)}</div>
}

export default ConformityLinkWidget
